import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { safeLog } from '@/utils/safeLogger';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Loader2, AlertCircle, CheckCircle2, RefreshCw } from 'lucide-react';

export default function Auth() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [autoLoginInitiated, setAutoLoginInitiated] = useState(false);
  const [showFallbackButton, setShowFallbackButton] = useState(false);
  const [showTimeoutHint, setShowTimeoutHint] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  
  const redirectTo = searchParams.get('redirect') || '/settings';
  const isDialogMode = searchParams.get('mode') === 'dialog';
  const qrSessionId = searchParams.get('qr_session');
  const [qrAuthSuccess, setQrAuthSuccess] = useState(false);

  // Redirect if already logged in
  useEffect(() => {
    const checkAuthAndRedirect = async () => {
      if (isDialogMode) {
        safeLog.info('📱 Dialog mode: Ignoring existing session, showing login form');
        return;
      }
      
      if (user) {
        safeLog.info('[Auth] Session detected, redirecting to:', { redirectTo });
        setIsRedirecting(true);
        navigate(redirectTo, { replace: true });
      }
    };
    
    checkAuthAndRedirect();
  }, [user, navigate, redirectTo, isDialogMode]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        safeLog.info(`[Auth] onAuthStateChange event: ${event} session: ${!!session} isDialogMode: ${isDialogMode}`);
        
        if (event === 'SIGNED_IN' && session && !isDialogMode) {
          const redirectPath = sessionStorage.getItem('auth_redirect') || redirectTo;
          sessionStorage.removeItem('auth_redirect');
          safeLog.info('[Auth] SIGNED_IN event, redirecting to:', { redirectPath });
          setIsRedirecting(true);
          navigate(redirectPath, { replace: true });
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [navigate, redirectTo, isDialogMode]);

  // Auto-start Microsoft login in dialog mode
  useEffect(() => {
    if (isDialogMode && !autoLoginInitiated && !user) {
      setAutoLoginInitiated(true);
      // Small delay to ensure component is fully mounted
      const timer = setTimeout(() => {
        handleMicrosoftSignIn();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isDialogMode, autoLoginInitiated, user]);

  // Show fallback button after 5 seconds if auto-login doesn't redirect
  useEffect(() => {
    if (isDialogMode && autoLoginInitiated) {
      const timer = setTimeout(() => {
        setShowFallbackButton(true);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isDialogMode, autoLoginInitiated]);

  // Show timeout hint after 8 seconds if redirect is slow
  useEffect(() => {
    if (isRedirecting && !showTimeoutHint) {
      const timer = setTimeout(() => {
        setShowTimeoutHint(true);
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [isRedirecting, showTimeoutHint]);
  const handleMicrosoftSignIn = () => {
    const redirectAfterAuth = redirectTo || '/settings';
    sessionStorage.setItem('auth_redirect', redirectAfterAuth);

    // In Office Dialog / embedded contexts popups are frequently blocked.
    // For dialog mode we always redirect in the same window.
    if (isDialogMode) {
      window.location.assign('/auth-start-microsoft');
      return;
    }
    
    const width = 500;
    const height = 700;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;
    
    const popup = window.open(
      '/auth-start-microsoft',
      'Microsoft Login',
      `width=${width},height=${height},left=${left},top=${top},popup=1`
    );

    // If popup is blocked, fall back to same-window redirect.
    if (!popup) {
      safeLog.warn('[Auth] Popup blocked, falling back to redirect');
      window.location.assign('/auth-start-microsoft');
      return;
    }
    
    const handleMessage = async (event: MessageEvent) => {
        if (event.data.type === 'microsoft-auth-success') {
        safeLog.info('[Auth] Microsoft login successful');
        popup?.close();
        window.removeEventListener('message', handleMessage);
        
        if (event.data.session) {
          safeLog.info('[Auth] Setting session from popup data...');
          await supabase.auth.setSession({
            access_token: event.data.session.access_token,
            refresh_token: event.data.session.refresh_token
          });
          safeLog.info('[Auth] Session set in parent window');
          
          // Dialog mode: Send message to Office Add-in via messageParent
          if (isDialogMode && (window as any).Office?.context?.ui?.messageParent) {
            safeLog.info('[Auth] Dialog mode: Sending message to Office Add-in');
            (window as any).Office.context.ui.messageParent(JSON.stringify({
              type: 'auth-success',
              session: event.data.session,
              graphToken: event.data.graphToken
            }));
          } else {
            // Standard: Redirect to settings page
            safeLog.info('[Auth] Initiating redirect to:', { redirectTo });
            setTimeout(() => {
              navigate(redirectTo, { replace: true });
            }, 1000);
          }
        } else {
          safeLog.error('[Auth] No session data in message');
        }
      }
    };
    
    window.addEventListener('message', handleMessage);
  };


  // Show success screen for QR authentication
  if (qrAuthSuccess) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12 space-y-4">
            <CheckCircle2 className="h-16 w-16 text-green-500" />
            <h2 className="text-2xl font-bold text-center">Authentication Successful!</h2>
            <p className="text-muted-foreground text-center">
              You can now close this window and return to your Outlook Add-In.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show loading screen during redirect
  if (isRedirecting) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Redirecting...</p>
        
        {showTimeoutHint && (
          <div className="mt-4 p-4 bg-muted rounded-lg max-w-md text-center">
            <p className="text-sm text-muted-foreground mb-3">
              Taking longer than expected? This might be a connection issue.
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => {
                setIsRedirecting(false);
                setShowTimeoutHint(false);
              }}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Try Again
            </Button>
          </div>
        )}
      </div>
    );
  }

  // Dialog mode: Show auto-login loading screen
  if (isDialogMode) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4 p-8">
          <Loader2 className="h-10 w-10 animate-spin mx-auto text-primary" />
          <p className="text-sm text-muted-foreground">
            Redirecting to Microsoft login...
          </p>
          
          {/* Fallback button after 5 seconds */}
          {showFallbackButton && (
            <div className="space-y-3 pt-4">
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground">
                  If you're not redirected automatically, click below to continue.
                </p>
              </div>
              <Button 
                type="button" 
                variant="outline" 
                size="lg"
                className="w-full"
                onClick={handleMicrosoftSignIn}
              >
                <svg className="mr-2 h-4 w-4" viewBox="0 0 21 21">
                  <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                  <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                  <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                </svg>
                Sign in with Microsoft
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 py-16">
        <div className="container px-4 max-w-md mx-auto">
          <Card>
            <CardHeader>
              <CardTitle>Welcome to Email2Contact</CardTitle>
              <CardDescription>
                Sign in with your Microsoft account to access your settings
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <Button 
                  type="button" 
                  variant="outline" 
                  size="lg"
                  className="w-full"
                  onClick={handleMicrosoftSignIn}
                  disabled={isLoading}
                >
                  <svg className="mr-2 h-4 w-4" viewBox="0 0 21 21">
                    <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                    <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                    <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                    <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                  </svg>
                  {isLoading ? 'Signing in...' : 'Sign in with Microsoft'}
                </Button>

                <p className="text-sm text-muted-foreground text-center">
                  Sign in with your Microsoft account to access Email2Contact
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      
    </div>
  );
}